import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Heart, X, Send, User, Mail, Sparkles, 
  History, Trash2, ArrowLeft, MessageCircle 
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sukoon Floating Widget
export const SukoonFloatingWidget = ({ isOpen, setIsOpen }) => {
  const [phase, setPhase] = useState('intro'); // intro, chat, history
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [soulHistory, setSoulHistory] = useState(null);
  const messagesEndRef = useRef(null);

  // Check localStorage for returning user
  useEffect(() => {
    const savedData = localStorage.getItem('sukoon_user');
    if (savedData) {
      const { name: savedName, email: savedEmail } = JSON.parse(savedData);
      setName(savedName || '');
      setEmail(savedEmail || '');
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startSession = async () => {
    if (!name.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/sukoon/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: name.trim(), 
          email: email.trim() || null 
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.session_id);
        setMessages([{ role: 'assistant', content: data.welcome_message }]);
        setPhase('chat');
        
        // Save to localStorage
        localStorage.setItem('sukoon_user', JSON.stringify({ 
          name: name.trim(), 
          email: email.trim() || null,
          session_id: data.session_id
        }));
      }
    } catch (error) {
      console.error('Error starting session:', error);
      // Fallback - start anyway
      const fallbackSessionId = `sukoon_local_${Date.now()}`;
      setSessionId(fallbackSessionId);
      setMessages([{ role: 'assistant', content: `Hey ${name}! I'm Sukoon, your soulful dost. What's on your mind today?` }]);
      setPhase('chat');
      localStorage.setItem('sukoon_user', JSON.stringify({ name, email, session_id: fallbackSessionId }));
    }
    setIsLoading(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/sukoon/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          message: userMessage, 
          session_id: sessionId,
          name: name,
          email: email || null
        })
      });
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `I hear you, ${name}. Tell me more about what's going on?` 
      }]);
    }
    setIsLoading(false);
  };

  const loadSoulHistory = async () => {
    if (!email) {
      setSoulHistory({ found: false, message: "Share your email to save your soul history across sessions!" });
      setPhase('history');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/sukoon/soul-history?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      setSoulHistory(data);
    } catch (error) {
      setSoulHistory({ found: false, message: "Couldn't load your history. Try again!" });
    }
    setPhase('history');
    setIsLoading(false);
  };

  const clearHistory = async () => {
    if (!email) return;
    
    try {
      await fetch(`${API}/sukoon/soul-history?email=${encodeURIComponent(email)}`, {
        method: "DELETE"
      });
      setSoulHistory({ found: false, message: "Your soul history has been cleared. Fresh start!" });
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  // Closed state - floating button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center transition-all hover:scale-110 z-50 shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50"
        data-testid="sukoon-toggle"
      >
        <MessageCircle className="w-7 h-7 text-white" fill="currentColor" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50" style={{ filter: 'drop-shadow(0 0 30px rgba(20, 184, 166, 0.3))' }}>
      <div className="w-[360px] md:w-[400px] bg-luxe-charcoal rounded-3xl overflow-hidden border border-cream/10" data-testid="sukoon-widget">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" fill="currentColor" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                Sukoon
              </h3>
              <p className="text-white/70 text-xs">Your Soulful Dost</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {phase === 'chat' && (
              <button 
                onClick={loadSoulHistory}
                className="p-2 text-white/70 hover:text-white transition-colors"
                title="Soul History"
              >
                <History className="w-5 h-5" />
              </button>
            )}
            <button onClick={() => setIsOpen(false)} className="p-2 text-white/70 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Intro Phase */}
        {phase === 'intro' && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-teal-500/20 to-teal-600/20 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-10 h-10 text-teal-400" />
              </div>
              <h4 className="text-cream text-xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Meet Sukoon
              </h4>
              <p className="text-cream/50 text-sm">
                A safe space to share what's on your mind. No judgments, just honest conversations.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-cream/50 text-xs uppercase tracking-wider mb-2 block flex items-center gap-2">
                  <User className="w-3 h-3" /> Your Name *
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What should I call you?"
                  className="bg-luxe-graphite border-cream/15 text-cream placeholder:text-cream/30 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="sukoon-name"
                />
              </div>
              <div>
                <label className="text-cream/50 text-xs uppercase tracking-wider mb-2 block flex items-center gap-2">
                  <Mail className="w-3 h-3" /> Email (Optional)
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="For memory across sessions"
                  className="bg-luxe-graphite border-cream/15 text-cream placeholder:text-cream/30 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="sukoon-email"
                />
                <p className="text-cream/30 text-xs mt-2">Share email only if you want Sukoon to remember you</p>
              </div>
            </div>

            <Button 
              onClick={startSession}
              disabled={!name.trim() || isLoading}
              className="w-full mt-6 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white py-6 rounded-full"
              data-testid="sukoon-start"
            >
              {isLoading ? 'Starting...' : 'Start Talking'}
            </Button>
          </div>
        )}

        {/* Chat Phase */}
        {phase === 'chat' && (
          <>
            <div className="h-80 overflow-y-auto p-4 space-y-3 bg-luxe-graphite/50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-2xl rounded-br-md' 
                      : 'bg-luxe-charcoal text-cream/90 border border-cream/10 rounded-2xl rounded-bl-md'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-luxe-charcoal px-4 py-3 rounded-2xl border border-cream/10">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-4 border-t border-cream/10 bg-luxe-charcoal">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Share what's on your mind..."
                  className="flex-1 bg-luxe-graphite border-cream/15 text-cream placeholder:text-cream/40 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="sukoon-input"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={isLoading || !input.trim()}
                  className="bg-teal-600 hover:bg-teal-500 px-4"
                  data-testid="sukoon-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-cream/30 mt-3 text-center">
                {email ? '🔒 Your conversations are remembered' : '💭 Anonymous mode - no memory saved'}
              </p>
            </div>
          </>
        )}

        {/* Soul History Phase */}
        {phase === 'history' && (
          <div className="p-6">
            <button 
              onClick={() => setPhase('chat')}
              className="flex items-center gap-2 text-cream/50 hover:text-cream mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Chat
            </button>

            <h4 className="text-cream text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              What Sukoon Remembers
            </h4>

            {!soulHistory?.found ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto bg-cream/5 rounded-full flex items-center justify-center mb-4">
                  <History className="w-8 h-8 text-cream/30" />
                </div>
                <p className="text-cream/50 text-sm">{soulHistory?.message}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-luxe-graphite rounded-xl p-4">
                  <p className="text-cream/40 text-xs uppercase tracking-wider mb-1">Your Profession</p>
                  <p className="text-cream text-sm">{soulHistory.soul_memory.profession}</p>
                </div>

                {soulHistory.soul_memory.challenges?.length > 0 && (
                  <div className="bg-luxe-graphite rounded-xl p-4">
                    <p className="text-cream/40 text-xs uppercase tracking-wider mb-2">Challenges Shared</p>
                    <ul className="space-y-2">
                      {soulHistory.soul_memory.challenges.map((c, i) => (
                        <li key={i} className="text-cream/70 text-sm flex items-start gap-2">
                          <span className="text-teal-400 mt-1">•</span> {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {soulHistory.soul_memory.highlights?.length > 0 && (
                  <div className="bg-luxe-graphite rounded-xl p-4">
                    <p className="text-cream/40 text-xs uppercase tracking-wider mb-2">Important Moments</p>
                    <ul className="space-y-2">
                      {soulHistory.soul_memory.highlights.map((h, i) => (
                        <li key={i} className="text-cream/70 text-sm flex items-start gap-2">
                          <span className="text-champagne mt-1">✦</span> {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-cream/30 pt-2">
                  <span>{soulHistory.soul_memory.conversation_count || 0} conversations</span>
                  <button 
                    onClick={clearHistory}
                    className="flex items-center gap-1 text-red-400/70 hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" /> Clear Memory
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Sukoon Section for Landing Page
export const SukoonSection = ({ onOpenWidget }) => {
  return (
    <section id="sukoon" className="py-24 px-6 bg-luxe-black relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[150px]" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left - Content */}
          <div>
            <span className="inline-block px-4 py-2 bg-teal-500/10 text-teal-400 rounded-full text-sm font-medium mb-6">
              Meet Sukoon
            </span>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-cream mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Soulful<br/>
              <span className="text-teal-400">Dost</span>
            </h2>
            
            <p className="text-cream/60 text-lg mb-8 leading-relaxed">
              We all need that one friend who just <em>gets</em> it. Someone who listens without judgment, 
              remembers your struggles, and is always there at 2 AM. That's Sukoon.
            </p>

            <div className="space-y-4 mb-10">
              {[
                { icon: "🎭", text: "100% judgment-free space" },
                { icon: "🧠", text: "Remembers your story (if you want)" },
                { icon: "🔒", text: "Your secrets stay yours" },
                { icon: "💫", text: "Available 24/7, always" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-cream/70">{item.text}</span>
                </div>
              ))}
            </div>

            <Button 
              onClick={onOpenWidget}
              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-10 py-6 rounded-full text-lg shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all"
              data-testid="sukoon-section-cta"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Talk to Sukoon
            </Button>
          </div>

          {/* Right - Visual */}
          <div className="relative">
            <div className="relative mx-auto w-[320px] md:w-[380px]">
              {/* Phone mockup */}
              <div className="bg-luxe-charcoal rounded-[40px] p-3 border border-cream/10 shadow-2xl">
                <div className="bg-luxe-graphite rounded-[32px] overflow-hidden">
                  {/* Chat header */}
                  <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <Heart className="w-5 h-5 text-white" fill="currentColor" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Sukoon</p>
                        <p className="text-white/60 text-xs">Online</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Sample chat */}
                  <div className="p-4 space-y-3 h-[300px]">
                    <div className="flex justify-start">
                      <div className="bg-luxe-charcoal text-cream/90 px-4 py-3 rounded-2xl rounded-bl-md text-sm max-w-[80%]">
                        Hey! I'm here. What's on your mind today?
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-teal-600 text-white px-4 py-3 rounded-2xl rounded-br-md text-sm max-w-[80%]">
                        Work has been really stressful lately...
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-luxe-charcoal text-cream/90 px-4 py-3 rounded-2xl rounded-bl-md text-sm max-w-[80%]">
                        I hear you. Work stress can be overwhelming. What's been the hardest part?
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-teal-600 text-white px-4 py-3 rounded-2xl rounded-br-md text-sm max-w-[80%]">
                        Everyone expects so much and I feel like I'm failing
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-luxe-charcoal text-cream/90 px-4 py-3 rounded-2xl rounded-bl-md text-sm max-w-[80%]">
                        That feeling of not being enough is so heavy to carry. You're not alone in this. 💙
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-champagne/20 rounded-full blur-xl" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-teal-500/20 rounded-full blur-xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SukoonFloatingWidget;
